import { CopyOutlined } from '@ant-design/icons';
import { Button, message, Modal } from 'antd';
import FormField from 'components/FormField';
import StatusButton from 'components/StatusButton';
import { getSegmentsLength } from 'components/TemplateEditor/Editor';
import useModal from 'hooks/useModal';
import useSmartField from 'hooks/useSmartField';
import { useState, useMemo } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useTranslate } from '@tolgee/react';
import { Element } from 'react-scroll';
import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { FV } from './share';
import { translateTemplate } from 'views/Aide/Scenario/PolicyForm/TranslateTool';
import {
  EditorWrap,
  LeadingLine,
  RegionExecuteContextsFieldProps,
  TaskLeftWrap,
  TaskWrapper,
  TitleLine,
} from 'views/Aide/Scenario/PolicyForm/NoRegionForm/RegionExecuteContextsField';
import ExecuteContextsFieldV2 from 'views/Aide/Scenario/PolicyForm/ExecuteContextsFieldV2';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { useReactScrollContext } from 'views/Aide/components/ReactScrollContext';
import { removeParagraph, removeSticker } from 'components/TemplateEditor/utils/remove';
import type { Concept } from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import {
  ScenarioResult,
  ScenarioResultProps,
} from '../../../../Setting/AutoEmit/components/Result';
import {
  getResultContexts,
  ResultText,
} from 'views/Aide/Scenario/PolicyForm/BilingualExecuteContextField';
import EditorPreviewer from 'components/EditorPreviewer';

function TodoRegionExecuteContextsField(
  props: Pick<RegionExecuteContextsFieldProps, 'disabled' | 'validate'>,
) {
  const { disabled = false } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { createRegionContextId } = useReactScrollContext();

  const {
    field: { value: regionExecuteContexts = [], onChange: onRegionExecuteContextsChange },
  } = useSmartField<FV['regionExecuteContexts']>('regionExecuteContexts');

  const {
    field: { value: recommendTemplates },
  } = useSmartField<FV['recommendTemplates']>('recommendTemplates');

  const {
    field: { value: resultType },
  } = useSmartField<FV['resultType']>('resultType');

  const copyModal = useModal();

  const [refresh, setRefresh] = useState(1);

  const extraConcepts = useMemo<Concept[] | undefined>(() => {
    if (resultType === 'REISSUE_GOOD_SENDED') {
      return [
        {
          id: '物流单号',
          label: t('物流单号'),
        },
      ];
    }
    if (resultType === 'PARTIAL_REFUND_ASK_BUYER') {
      return [
        {
          id: '退款金额',
          label: t('trade.退款金额'),
        },
      ];
    }
    if (resultType === 'USE_METHOD_ERROR_TELL_METHOD') {
      return [
        {
          id: '使用方法',
          label: t('使用方法'),
        },
      ];
    }
    if (
      resultType === 'OUT_STOCK_ASK_BUYER_WAIT' ||
      resultType === 'EXPEDITED_DELIVERY_TELL_BUYER'
    ) {
      return [
        {
          id: '发货时间',
          label: t('发货时间'),
        },
      ];
    }
    if (resultType === 'OFFER_COMPENSATION_ASK_BUYER') {
      return [
        {
          id: '补偿金额',
          label: t('补偿金额'),
        },
      ];
    }
    if (resultType === 'OFFER_COMPENSATION_TELL_PROCESS') {
      return [
        {
          id: '补偿方式',
          label: t('补偿方式'),
        },
      ];
    }
    if (resultType === 'OFFER_COUPONS_ASK_BUYER') {
      return [
        {
          id: '优惠券面值',
          label: t('优惠券面值'),
        },
      ];
    }
    if (resultType === 'OFFER_COUPONS_SENT') {
      return [
        {
          id: '优惠券时限',
          label: t('优惠券时限'),
        },
      ];
    }
    if (resultType === 'ORDER_CANCEL_REJECT_OTHER') {
      return [
        {
          id: '拒绝原因',
          label: t('拒绝原因'),
        },
      ];
    }
    if (resultType === 'ORDER_CANCEL_TELL_REASON') {
      return [
        {
          id: '取消原因',
          label: t('取消原因'),
        },
      ];
    }
  }, [t, resultType]);

  const list = regionExecuteContexts.map(({ enable = true, region, executeContext }, index) => {
    if (!enable) return null;
    const copy = !disabled && index === 0 && regionExecuteContexts.length > 1 && (
      <>
        <Button
          type="link"
          disabled={getSegmentsLength(executeContext.content, 'text') === 0}
          onClick={copyModal.show}
        >
          <CopyOutlined />
          {t('aide.复用内容至其他全部站点')}
        </Button>
        <Modal
          title={t('aide.提示')}
          visible={copyModal.visible}
          footer={[
            <Button
              key="back"
              onClick={() => {
                const nextExecuteContext = {
                  content: executeContext.content.filter(removeSticker),
                  translatedContent: executeContext.translatedContent
                    .filter(removeSticker)
                    .filter(removeParagraph),
                };

                const next = regionExecuteContexts.map((context, index) => {
                  if (index === 0) {
                    return context;
                  }

                  return {
                    ...context,
                    executeContext: nextExecuteContext,
                  };
                });

                onRegionExecuteContextsChange(next);

                setRefresh((_) => _ + 1);

                copyModal.hide();
              }}
            >
              {t('aide.不需要翻译')}
            </Button>,
            <StatusButton
              key="submit"
              type="primary"
              onClick={() => {
                const content = executeContext.content.filter(removeSticker);

                return regionExecuteContexts
                  .reduce<Promise<Required<FV>['regionExecuteContexts']>>((res, cur, index) => {
                    return res.then((list) => {
                      if (index === 0) {
                        list.push(cur);

                        return list;
                      }

                      const { region, ...rest } = cur;

                      const { [toLowerCase(region)]: language } = regionLanguageMap;

                      return translateTemplate(content, language, recommendTemplates, 'GOOGLE', {
                        from: 'regions',
                      }).then((content) => {
                        list.push({
                          region,
                          ...rest,
                          executeContext: {
                            content,
                            translatedContent: [],
                          },
                        });

                        return list;
                      });
                    });
                  }, Promise.resolve([]))
                  .then((res) => {
                    batchedUpdates(() => {
                      onRegionExecuteContextsChange(res);

                      setRefresh((_) => _ + 1);

                      copyModal.hide();
                    });
                  })
                  .catch(() => {
                    message.error(t('translation.操作失败，请稍后再试'));
                  });
              }}
            >
              {t('aide.需要翻译')}
            </StatusButton>,
          ]}
          onCancel={copyModal.hide}
        >
          {t('aide.复制任务提示')}
        </Modal>
      </>
    );

    const title = (
      <TitleLine
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingRight: '32px',
        }}
      >
        <div>
          <LeadingLine />
          {t('aide.发送内容')}
          <span>-</span>
          {getRegionLabel(region)}
        </div>
        {copy}
      </TitleLine>
    );

    const {
      validate = (value) => {
        if (!value) {
          return t('refundSetting.请输入回复内容');
        }

        const {
          executeContext: { content, translatedContent },
        } = value;

        const hasText = content.some((v) => v.type === 'paragraph');
        if (hasText) {
          const contentLength = getSegmentsLength(content, 'text');
          const translatedContentLength = getSegmentsLength(translatedContent, 'text');
          const contentLimit = (() => {
            const { ALIEXPRESS, MERCADO, ...others } = platformExecuteContentLimitLengthMap;
            return Math.min(...Object.values(others));
          })();
          if (contentLength === 0) {
            return t('refundSetting.请输入回复内容');
          }

          if (contentLength > contentLimit || translatedContentLength > contentLimit) {
            return t('refundSetting.回复内容不可超出x字', { x: contentLimit });
          }
        } else {
          const contentLength = getSegmentsLength(content, 'image');
          if (contentLength === 0) {
            return t('refundSetting.请输入回复内容');
          }
        }

        return undefined;
      },
    } = props;

    const formField = (
      <FormField<Required<FV>['regionExecuteContexts'][number]>
        name={`regionExecuteContexts.${index}`}
        validate={validate}
      >
        {({ field: { value, onChange, onBlur }, meta: { touched } }) => {
          const editor = (
            <ExecuteContextsFieldV2
              supportTranslate
              allowDividing={false}
              region={region}
              msgTypes={['text', 'image', 'video']}
              value={value.executeContext}
              onChange={(executeContext) => {
                onChange({
                  ...value,
                  executeContext,
                });

                setTimeout(() => {
                  if (!touched) {
                    onBlur();
                  }
                });
              }}
              error=""
              refresh={refresh}
              recommendTemplates={recommendTemplates}
              extraConcepts={extraConcepts}
              scenarioType="TODO"
            />
          );

          return (
            <EditorWrap>
              <div style={disabled ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}>
                {editor}
              </div>
            </EditorWrap>
          );
        }}
      </FormField>
    );

    const result = (
      <FormField<Required<FV>['regionExecuteContexts'][number]>
        name={`regionExecuteContexts.${index}`}
        noStyle
      >
        {({ field: { value: _value } }) => {
          const demoResult = (() => {
            const value = _value.executeContext;
            if (!value) {
              return [];
            }
            return [value].reduce<ScenarioResultProps['list']>((res, executeContext) => {
              const resultContext = getResultContexts(executeContext, true);

              res.push(
                ...(resultContext
                  ? resultContext.map((context) => {
                      return {
                        type: 'reply',
                        content: (
                          <ResultText>
                            <EditorPreviewer value={context} />
                          </ResultText>
                        ),
                      } as const;
                    })
                  : [
                      {
                        type: 'reply',
                        content: '',
                      } as const,
                    ]),
              );

              return res;
            }, []);
          })();
          return <ScenarioResult list={demoResult} />;
        }}
      </FormField>
    );

    return (
      <Element key={region} name={createRegionContextId(region)}>
        <TaskWrapper>
          <TaskLeftWrap>
            {title}
            {formField}
          </TaskLeftWrap>
          <div>
            <h4 className="text-[var(--fett-text-primary)]">{t('marketing.示例')}</h4>
            {result}
          </div>
        </TaskWrapper>
      </Element>
    );
  });

  return <>{list}</>;
}

export default TodoRegionExecuteContextsField;
